/**
 * Markers used in DSL context bindings to indicate the source of context values.
 * These are used in `contextBindings[].sourceFieldId` to tell AutoDetectField
 * where to resolve a context key from.
 */
export enum DSLContextMarker {
  /** Indicates the value comes from the top-level form context (e.g., companyId) */
  FORM_CONTEXT = '__form_context__',
  /** Indicates the value comes from an entity-level context (e.g., corporationId from EntitySelectOrCreate) */
  ENTITY_CONTEXT = '__entity_context__',
}

/**
 * Prefix for storing resolved display labels alongside raw field values.
 * Used by AutoDetectField to persist human-readable labels for enum, date,
 * and other schema-resolved field types so the summary view can display them.
 */
export const RESOLVED_LABEL_PREFIX = '__label__';

/**
 * Key holding a repeatable entry's own identity, written by RepeatableModuleRenderer.
 *
 * Deliberately `id` and not a `__`-prefixed key: the canonical form response is
 * addressed with paths like `partners[id=abc].firstName`, which resolvePath already
 * supports, so other fields can reference an entry that survives reorder and removal.
 * The backend canonicaliser rebuilds each entry from declared components only, so it
 * has to preserve this key explicitly.
 */
export const ENTRY_ID_KEY = 'id';

/**
 * Category ID for Commenda OS components in the form builder palette.
 */
export const COMMENDA_OS_CATEGORY_ID = 'commenda-os';

/**
 * Data source type identifier for Commenda OS DSL fields.
 */
export const COMMENDA_OS_DATA_SOURCE = 'commenda-os' as const;

/**
 * Upload cap applied when a template's file field does not set `maxSizeInMB`.
 * Uploads go straight to S3 on a presigned PUT, so this client-side check is the
 * only ceiling — tax documents regularly run past the old 10MB.
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 50;

/**
 * Key on a KeyPersons field value holding `personId -> display name`, written by
 * KeyPersonsComponent so a later field offering those people can label them.
 *
 * A person already on the company is selected by id and authors no entry, so their
 * name exists only in the list that component fetched. `__`-prefixed, so the
 * canonicaliser drops it rather than storing a copy of names it can resolve itself.
 */
export const PERSON_LABELS_KEY = '__personLabels__';
